import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationPopupComponent } from 'src/app/shared/component/confirmation-popup/confirmation-popup.component';
import { Context } from 'src/app/shared/models/context';
import { Message } from 'src/app/shared/models/domain';
import { AccountTransaction, AccountTransactionEntry } from 'src/app/shared/models/finance';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DataService } from 'src/app/shared/services/data.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import { ManageTransactionEntryComponent } from './manage-transaction-entry/manage-transaction-entry.component';

@Component({
  selector: 'app-manage-transaction',
  templateUrl: './manage-transaction.component.html',
  styleUrls: ['./manage-transaction.component.scss']
})
export class ManageTransactionComponent implements OnInit {
  @Input() transaction: AccountTransaction;
  @Input() context: Context;
  @Input() transactions: AccountTransaction[] = [];
  @Input() transactionEntries: AccountTransactionEntry[] = [];
  public transactionForm: FormGroup;
  public minDate: string = "";
  public maxDate: string = "";
  public submitted: boolean = false;
  private modified: boolean = false;
  public isLoading: boolean = true;

  constructor(
    private financeService: FinanceService,
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private authService: AuthService,
    private commonService: CommonService,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.setTransaction(this.transaction);
    if (this.transactionForm.value.id) {
      this.getAccountTransactionEntries();
    }
  }

  public formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  public setTransaction(transaction?: AccountTransaction) {
    if (!transaction)
      transaction = {
        id: null,
        date: null,
        refId: null,
        disabled: false,
        particular: null,
        invoiceNo:null
      } as AccountTransaction;
    this.transactionForm = this.fb.group({
      id: this.fb.control(transaction?.id || null),
      date: this.fb.control(transaction.date ? this.formatDate(transaction.date) : this.formatDate(new Date().toISOString()),
        [Validators.required] ),
      refId: this.fb.control(transaction.refId, []),
      invoiceNo: this.fb.control(transaction.invoiceNo, Validators.pattern(/^[a-zA-Z0-9-]*$/) ),
      particular: this.fb.control(transaction.particular, [Validators.required]),
      disabled: this.fb.control(transaction.disabled, []),
    });
    this.transactionForm.valueChanges.subscribe(value => this.modified = true);
  }

  get t() {
    return this.transactionForm.controls;
  }

  public getTransaction(){
    this.financeService.getAccountTransaction(this.transactionForm.value.id).subscribe({
      next: (transaction:AccountTransaction) => {
        this.transaction = transaction;
      }
    });
  }

  public saveTransaction(inProgress?: any) {
    this.submitted = true;
    if (this.transactionForm.valid) {
      const transaction: AccountTransaction = this.transactionForm.value;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Transaction ${transaction.refId} has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (transaction.id) {
        this.financeService.updateAccountTransaction(transaction.id, transaction).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.getTransaction();
              this.commonService.setToastr(200, response.text);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
          },
        });
      } else {
        inProgress.value = false;
        this.financeService.createAccountTransaction(transaction).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.transactionForm.patchValue({ id: parseInt( response.payload) });
              this.transaction = { ...this.transaction, id: parseInt( response.payload)};
              this.getTransaction();
              this.commonService.setToastr(200, response.text);
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
          },
        });
      }
    }
  }

  //TRANSACTION ENTRIES

  private getAccountTransactionEntries() {
    if (this.context?.employeeId) {
      this.transactionEntries = [];
      this.financeService.getAccountTransactionEntries(this.transactionForm.value.id).subscribe({
        next: (transactionEntries: AccountTransactionEntry[]) => {
          this.transactionEntries = transactionEntries.sort((a, b) => a.id - b.id);
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  public deleteTransactionEntry(transactionEntries: AccountTransactionEntry, index: number) {
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.title = "Transaction Entry deletion";
    modalRef.componentInstance.prefixMessage = "Are you sure you want to delete";
    modalRef.componentInstance.message = "Transaction Entry";
    modalRef.componentInstance.label = 'transaction entry';
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.financeService.deleteAccountTransactionEntry(transactionEntries.id).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.commonService.setToastr(200, 'The transaction entry has been successfully deleted!');
              this.transactions.splice(index, 1);
              this.getAccountTransactionEntries();
            } else {
              this.commonService.setToastr(500, 'Unable to complete the action. Please try again.');
            }
          },
          error: (error: HttpErrorResponse) => {
            this.commonService.setToastr(500, error?.error?.name || error.error);
          },
        });
      }
    });
  }

  public deleteAllTransactionEntries() {
    const modalRef = this.modalService.open(ConfirmationPopupComponent);
    modalRef.componentInstance.title = "Delete All Transaction Entries";
    modalRef.componentInstance.message = "Are you sure you want to delete all transaction entries?";
    modalRef.result.then((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.financeService.getAccountTransactionEntries(this.transactionForm.value.id).subscribe(entries => {
          const ids = entries.map(entry => entry.id);
          this.financeService.deleteAllTransactionEntries(ids).subscribe(() => {
            this.commonService.setToastr(200, 'All transaction entries deleted successfully!');
            this.transactionEntries = []; 
          });
        });
      }
    });
  }

  public upsertTransactionEntry(transactionEntry?: AccountTransactionEntry) {
    const modalRef = this.modalService.open(ManageTransactionEntryComponent, { size: 'lg', backdrop: 'static', windowClass: 'child-modal' });
    modalRef.componentInstance.transactionEntry = transactionEntry;
    modalRef.componentInstance.transactionEntries = this.transactionEntries;
    modalRef.componentInstance.transaction = this.transaction;
    modalRef.componentInstance.context = this.context;
    modalRef.result.then((response: Message) => {
      if (!response.failed) {
        this.getAccountTransactionEntries();
      }
    });
  }

  getTotalDebit(): number {
    return this.transactionEntries
      .filter(entry => entry.byAccount)
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);
  }
  
  getTotalCredit(): number {
    return this.transactionEntries
      .filter(entry => !entry.byAccount)
      .reduce((sum, entry) => sum + (entry.amount || 0), 0);
  }
  
  getBalance(): number {
    return this.getTotalDebit() - this.getTotalCredit();
  }

}

